import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField,
  FormControl, InputLabel, Select, MenuItem, Grid, CircularProgress, Box,
} from '@mui/material';
import {
  SlideUpTransition,
  useMinimizable,
  DialogControls,
  MinimizedBar,
} from '../../components/MinimizableDialog';
import { BeneficiaireCreate, BeneficiaireUpdate } from '../../types/beneficiaire';
import { beneficiairesAPI } from '../../services/api';
import AlertDialog from '../../components/AlertDialog';
import useAlert from '../../hooks/useAlert';
import useFormDraft from '../../hooks/useFormDraft';
import { extractErrorMessage } from '../../utils/errorHandler';

interface Props {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  editId?: number;
  minimized?: boolean;
  onMinimizedChange?: (minimized: boolean) => void;
}

const BeneficiaireFormDialog: React.FC<Props> = ({ open, onClose, onSuccess, editId, minimized: controlledMinimized, onMinimizedChange }) => {
  const [loading, setLoading] = useState(false);
  const { minimized, setMinimized, minimize, restore, dialogOpen } = useMinimizable(
    open,
    controlledMinimized,
    onMinimizedChange
  );
  const [formData, setFormData] = useState<BeneficiaireCreate>({
    name: '',
    type: 'individual',
    phone: '',
    email: '',
    rib: '',
    address: '',
  });
  const { alertState, showError, showSuccess, hideAlert } = useAlert();

  // Draft caching — only for new bénéficiaires (never when editing an existing row).
  const { saveDraft, loadDraft, clearDraft } = useFormDraft<{
    formData: BeneficiaireCreate;
  }>('beneficiaire', !editId);

  useEffect(() => {
    if (open && editId) {
      loadBeneficiaire();
    } else if (open) {
      const draft = loadDraft();
      if (draft) {
        setFormData(draft.formData);
      } else {
        setFormData({ name: '', type: 'individual', phone: '', email: '', rib: '', address: '' });
      }
    }
  }, [open, editId]);

  const loadBeneficiaire = async () => {
    if (!editId) return;
    try {
      setLoading(true);
      const data = await beneficiairesAPI.getById(editId);
      setFormData({
        name: data.name,
        type: data.type,
        phone: data.phone || '',
        email: data.email || '',
        rib: data.rib || '',
        address: data.address || '',
      });
    } catch (err) {
      showError('Erreur lors du chargement du bénéficiaire');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!formData.name.trim()) {
      showError('Le nom est obligatoire');
      return;
    }
    try {
      setLoading(true);
      if (editId) {
        await beneficiairesAPI.update(editId, formData as BeneficiaireUpdate);
        showSuccess('Bénéficiaire modifié avec succès');
      } else {
        await beneficiairesAPI.create(formData);
        showSuccess('Bénéficiaire créé avec succès');
      }
      clearDraft();
      onSuccess();
    } catch (err: any) {
      showError(extractErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  // Annuler = deliberate cancel: wipe inputs and discard any draft.
  const handleClose = () => {
    clearDraft();
    setFormData({ name: '', type: 'individual', phone: '', email: '', rib: '', address: '' });
    setMinimized(false);
    onClose();
  };

  // Backdrop click / Escape = accidental: keep the work as a draft, then close.
  const handleDialogClose = (
    _event: object,
    reason: 'backdropClick' | 'escapeKeyDown'
  ) => {
    if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
      saveDraft({ formData });
      onClose();
    }
  };

  return (
    <>
      <Dialog open={dialogOpen} onClose={handleDialogClose} maxWidth="sm" fullWidth TransitionComponent={SlideUpTransition}>
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            {editId ? 'Modifier le Bénéficiaire' : 'Nouveau Bénéficiaire'}
            <DialogControls color="#9C27B0" onMinimize={minimize} onClose={handleClose} />
          </Box>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth label="Nom *" value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Type</InputLabel>
                <Select value={formData.type} label="Type" onChange={(e) => setFormData({ ...formData, type: e.target.value })}>
                  <MenuItem value="employee">Employé</MenuItem>
                  <MenuItem value="individual">Individuel</MenuItem>
                  <MenuItem value="other">Autre</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth label="Téléphone" value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth label="Email" type="email" value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth label="RIB" value={formData.rib}
                onChange={(e) => setFormData({ ...formData, rib: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth label="Adresse" multiline rows={2} value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={loading}>Annuler</Button>
          <Button variant="contained" onClick={handleSubmit} disabled={loading}>
            {loading ? <CircularProgress size={24} /> : (editId ? 'Modifier' : 'Créer')}
          </Button>
        </DialogActions>
      </Dialog>
      <MinimizedBar
        open={open && minimized}
        color="#9C27B0"
        title={editId ? 'Modifier le Bénéficiaire' : 'Nouveau Bénéficiaire'}
        onRestore={restore}
        onClose={handleClose}
      />
      <AlertDialog {...alertState} onClose={hideAlert} onConfirm={hideAlert} />
    </>
  );
};

export default BeneficiaireFormDialog;
